<template>
  <v-card class="chat-messages">
    <v-card-title
      v-if="details"
      class="pb-4"
    >
      <div class="chat-messages__title underlined-hover">
        <router-link :to="{ name: 'project-single', params: { id: details.projectId } }">
          {{ details.projectName }}
        </router-link>
      </div>
      <div class="chat-messages__details">
        {{ getMembersCount }}
      </div>
    </v-card-title>

    <v-card-text
      class="chat-messages__list"
      :class="{ 'chat-messages__list--locked': locked }"
    >
      <template v-if="messages.length">
        <ChatSingleMessage
          v-for="(message, index) in messages"
          :key="message.id"
          :message="message"
          :grouped="index < messages.length - 1
            && messages[index].userId === messages[index + 1].userId"
        />
        <div
          v-if="showMore"
          class="chat-messages__loader"
        >
          <v-progress-circular
            v-intersect="onLoadMoreIntersect"
            indeterminate
            color="primary"
            size="24"
          />
        </div>
      </template>

      <div
        v-else
        class="chat-messages__empty"
      >
        Brak wiadomości
      </div>
    </v-card-text>

    <v-card-actions
      v-if="!locked"
      class="chat-messages__actions"
    >
      <v-form
        class="chat-input"
        @submit.prevent="submitMessage"
      >
        <v-btn
          class="chat-input__file"
          color="gray"
          icon
          @click="uploadAttachment"
        >
          <v-icon small>
            mdi-paperclip
          </v-icon>
        </v-btn>
        <v-textarea
          ref="messageInput"
          v-model="newMessage"
          auto-grow
          :rows="1"
          wrap="hard"
          hide-details
          class="chat-input__textarea"
          placeholder="Wpisz swoją wiadomość tutaj..."
          @keydown.enter.exact.prevent="submitMessage"
          @keydown.enter.shift.exact.prevent="newMessage += '\n'"
        />
        <v-btn
          class="chat-input__submit"
          color="primary"
          solo
          :loading="processing"
          :disabled="!newMessage.length"
          type="submit"
        >
          Wyślij
        </v-btn>
      </v-form>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import utils from '@/helpers/utils';
import ChatSingleMessage from '@/components/Chat/ChatSingleMessage.vue';
import debounce from 'lodash.debounce';

export default {
  components: {
    ChatSingleMessage,
  },

  props: {
    details: {
      type: Object,
      default: () => {},
    },
    messages: {
      type: Array,
      required: true,
    },
    locked: {
      type: Boolean,
      default: false,
    },
    processing: {
      type: Boolean,
      required: true,
    },
    showMore: {
      type: Boolean,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      isIntersectingLoadMore: false,
    };
  },

  computed: {
    newMessage: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    currentUserId() {
      return this.$store.state.auth.user.id;
    },

    getMembersCount() {
      return utils.getCounterWithSuffix(this.details.usersCount,
        'członek', 'członków', 'członków');
    },
  },

  mounted() {
    if (this.$refs.messageInput) this.$nextTick(this.$refs.messageInput.focus());
  },

  methods: {
    uploadAttachment() {
      this.$emit('upload');
    },

    submitMessage: debounce(function () {
      this.$emit('submit');
    }, 500, { leading: true }),

    onLoadMoreIntersect(entries) {
      this.isIntersectingLoadMore = entries[0].isIntersecting;
      if (this.isIntersectingLoadMore) this.$emit('load');
    },
  },
};
</script>

<style lang="scss" scoped>
.chat-messages {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 214px);

  &__title {
    max-width: calc(100% - 100px);
    text-overflow: ellipsis;
    overflow: hidden;
  }
  &__details {
    letter-spacing: 0.5px;
    font-size: 10px;
    font-weight: 400;
  }

  &__list {
    height: 100%;
    min-height: 200px;
    overflow-y: scroll;
    padding-left: 32px;
    padding-right: 32px;
    padding-bottom: 0;
    display: flex;
    flex-direction: column-reverse;

    &--locked {
      height: calc(100vh - 274px);
      padding-bottom: 16px;
    }
  }

  &__loader {
    min-height: 40px;
    display: block;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }

  &__single-message {
    background: $primary;
    color: white;
    margin-bottom: 16px;
    border-radius: 2px;
    padding: 16px;
    display: inline-block;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: 0px;
    margin-right: 35%;

    &--own {
      background: $bg-light;
      color: $darken;
      margin-right: unset;
      margin-left: 35%;
    }
  }

  &__actions {
    padding: 16px 32px;
  }
}

.chat-input {
  border-radius: 2px;
  background: $bg-light;
  padding: 8px;
  display: flex;
  align-items: center;
  width: 100%;

  &__file {
    display: block;
    margin-right: 8px;
    margin-left: 4px;
  }

  &__textarea {
    display: block;
    width: 100%;
    border-left: 1px solid $light-grey;
    padding-left: 16px;
    padding-right: 16px;
    padding-top: 0;
    max-height: 300px;
    overflow-y: auto;
  }
  &__textarea::v-deep textarea {
    padding:0;
  }
  &__textarea::v-deep > .v-input__control > .v-input__slot:after,
  &__textarea::v-deep > .v-input__control > .v-input__slot:before {
    bottom: 0;
  }

  &__submit {
    display: block;
  }
}
</style>
